import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, CreditCard, Smartphone, Landmark, ShieldCheck, ArrowRight, Repeat, Globe2 } from "lucide-react";
import api from "../../lib/api";
import { useSiteConfig } from "../../context/SiteConfigContext";
import SeoHead from "../../components/SeoHead";

const CURRENCY_META = {
    usd: { symbol: "$", label: "USD" },
    eur: { symbol: "€", label: "EUR" },
    gbp: { symbol: "£", label: "GBP" },
};

export default function Donate() {
    const { config } = useSiteConfig();
    const [packages, setPackages] = useState([]);
    const [currencies, setCurrencies] = useState(["usd"]);
    const [currency, setCurrency] = useState("usd");
    const [interval, setInterval] = useState("one_time");
    const [customMin, setCustomMin] = useState(5);
    const [customMax, setCustomMax] = useState(10000);
    const [selected, setSelected] = useState("advocate");
    const [customAmount, setCustomAmount] = useState("");
    const [donor, setDonor] = useState({ name: "", email: "", message: "" });
    const [redirecting, setRedirecting] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        api.get(`/donations/packages?currency=${currency}`).then(({ data }) => {
            setPackages(data.packages);
            setCurrencies(data.currencies || ["usd"]);
            setCustomMin(data.custom_min);
            setCustomMax(data.custom_max);
        }).catch(() => {});
    }, [currency]);

    const sym = CURRENCY_META[currency]?.symbol || "$";

    const sspRate = config?.donate_ssp_rate || 0;
    const sspEquivalent = (usdAmount) => sspRate > 0 ? `≈ SSP ${Math.round(usdAmount * sspRate).toLocaleString()}` : null;

    const proceed = async () => {
        setError(""); setRedirecting(true);
        try {
            const body = {
                origin_url: window.location.origin,
                currency,
                interval,
                donor_name: donor.name,
                donor_email: donor.email || undefined,
                message: donor.message,
            };
            if (selected === "custom") {
                const amt = parseFloat(customAmount);
                if (!amt || amt < customMin) { setError(`Minimum donation is ${sym}${customMin}`); setRedirecting(false); return; }
                body.custom_amount = amt;
            } else {
                body.package_id = selected;
            }
            const { data } = await api.post("/donations/create-session", body);
            window.location.href = data.url;
        } catch (e) {
            const d = e?.response?.data?.detail;
            setError(typeof d === "string" ? d : "Could not start checkout. Please try again or use mobile money / bank transfer below.");
            setRedirecting(false);
        }
    };

    if (!config) return null;

    return (
        <div data-testid="donate-page">
            <SeoHead pageKey="donate" />
            {/* Hero */}
            <section className="bg-gradient-to-br from-accent to-background border-b border-border">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-24 pb-16 grid lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-7">
                        <div className="text-xs uppercase tracking-[0.22em] text-primary mb-4 inline-flex items-center gap-2">
                            <Heart size={14} /> Support YACRRD
                        </div>
                        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.05] mb-6">{config.donate_hero_title}</h1>
                        <p className="text-lg text-muted-foreground max-w-2xl">{config.donate_hero_subtitle}</p>
                    </div>
                    <div className="lg:col-span-5">
                        <div className="bg-card border border-border rounded-sm p-6">
                            <div className="text-xs uppercase tracking-[0.22em] text-secondary mb-3">Where your gift goes</div>
                            <p className="text-base leading-relaxed">{config.donate_impact_text}</p>
                            <div className="mt-5 pt-5 border-t border-border flex items-center gap-2 text-xs text-muted-foreground">
                                <ShieldCheck size={14} className="text-secondary" /> Secure card payments via Stripe
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Form */}
            <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid lg:grid-cols-12 gap-10">
                <div className="lg:col-span-7" data-testid="donate-form-card">
                    {/* Toggles */}
                    <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
                        <div className="inline-flex items-center rounded-sm border border-border bg-card p-1" data-testid="interval-toggle">
                            <button
                                onClick={() => setInterval("one_time")}
                                className={`px-4 py-2 text-xs uppercase tracking-[0.18em] rounded-sm transition-colors ${interval === "one_time" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
                                data-testid="interval-one-time"
                            >One-time</button>
                            <button
                                onClick={() => setInterval("month")}
                                className={`px-4 py-2 text-xs uppercase tracking-[0.18em] rounded-sm transition-colors inline-flex items-center gap-1.5 ${interval === "month" ? "bg-secondary text-secondary-foreground" : "text-muted-foreground hover:text-foreground"}`}
                                data-testid="interval-monthly"
                            ><Repeat size={12} /> Monthly</button>
                        </div>

                        <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-muted-foreground">
                            <Globe2 size={14} /> Currency
                            <select value={currency} onChange={(e) => setCurrency(e.target.value)}
                                className="ml-2 px-3 py-2 rounded-sm border border-border bg-background text-foreground"
                                data-testid="currency-select">
                                {currencies.map((c) => <option key={c} value={c}>{CURRENCY_META[c]?.label || c.toUpperCase()}</option>)}
                            </select>
                        </div>
                    </div>

                    <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground mb-3 inline-flex items-center gap-2">
                        <CreditCard size={14} /> {interval === "month" ? "Recurring monthly gift" : "Give by card"}
                    </div>
                    <h2 className="font-display text-3xl mb-8">{interval === "month" ? "Become a monthly partner" : "Make a one-time donation"}</h2>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                        {packages.map((p) => (
                            <button
                                key={p.id}
                                onClick={() => setSelected(p.id)}
                                className={`p-5 rounded-sm border text-left transition-colors ${
                                    selected === p.id ? (interval === "month" ? "border-secondary bg-secondary/5" : "border-primary bg-primary/5") : "border-border hover:border-primary/60"
                                }`}
                                data-testid={`donate-package-${p.id}`}
                            >
                                <div className="font-display text-2xl">{sym}{p.amount}{interval === "month" && <span className="text-xs text-muted-foreground font-sans">/mo</span>}</div>
                                <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground mt-1">{p.label}</div>
                            </button>
                        ))}
                        <button
                            onClick={() => setSelected("custom")}
                            className={`p-5 rounded-sm border text-left transition-colors col-span-2 sm:col-span-4 ${
                                selected === "custom" ? "border-primary bg-primary/5" : "border-border hover:border-primary/60"
                            }`}
                            data-testid="donate-package-custom"
                        >
                            <div className="flex items-center gap-3">
                                <span className="font-display text-2xl">{sym}</span>
                                <input
                                    type="number" min={customMin} max={customMax} step="1"
                                    value={customAmount}
                                    onChange={(e) => { setCustomAmount(e.target.value); setSelected("custom"); }}
                                    placeholder={`Other amount (min ${sym}${customMin})`}
                                    className="flex-1 bg-transparent border-0 focus:outline-none text-lg"
                                    data-testid="donate-custom-amount"
                                    onClick={(e) => e.stopPropagation()}
                                />
                                {interval === "month" && <span className="text-xs text-muted-foreground">per month</span>}
                            </div>
                        </button>
                    </div>

                    <div className="space-y-4 mb-6">
                        <div className="grid sm:grid-cols-2 gap-4">
                            <input placeholder="Your name (optional)" value={donor.name} onChange={(e) => setDonor({ ...donor, name: e.target.value })}
                                className="px-4 py-3 rounded-sm border border-border bg-background text-sm" data-testid="donate-name" />
                            <input type="email" placeholder="Email for receipt (optional)" value={donor.email} onChange={(e) => setDonor({ ...donor, email: e.target.value })}
                                className="px-4 py-3 rounded-sm border border-border bg-background text-sm" data-testid="donate-email" />
                        </div>
                        <textarea placeholder="A short note to the YACRRD team (optional)" rows={2}
                            value={donor.message} onChange={(e) => setDonor({ ...donor, message: e.target.value })}
                            className="w-full px-4 py-3 rounded-sm border border-border bg-background text-sm resize-y" data-testid="donate-message" />
                    </div>

                    {error && <div className="text-sm text-destructive mb-4" data-testid="donate-error">{error}</div>}

                    <button onClick={proceed} disabled={redirecting}
                        className={`inline-flex items-center gap-2 px-7 py-3.5 rounded-sm text-sm font-medium disabled:opacity-60 ${
                            interval === "month" ? "bg-secondary text-secondary-foreground hover:bg-secondary/90" : "bg-primary text-primary-foreground hover:bg-primary/90"
                        }`}
                        data-testid="donate-submit"
                    >
                        {redirecting ? "Redirecting to Stripe…" : <>Continue to secure checkout <ArrowRight size={14} /></>}
                    </button>
                    <p className="text-xs text-muted-foreground mt-3">
                        {interval === "month"
                            ? "You'll be billed every month until you choose to cancel. Cancel anytime by emailing us — your gift will stop immediately."
                            : "You'll be redirected to Stripe to complete payment safely. We never store your card details."}
                    </p>
                </div>

                {/* Mobile money & bank */}
                <aside className="lg:col-span-5 space-y-5" data-testid="donate-other-methods">
                    <div className="bg-card border border-border rounded-sm p-6">
                        <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-3"><Smartphone size={18} className="text-secondary" /><h3 className="font-display text-xl">Mobile Money</h3></div>
                            {sspRate > 0 && <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">1 USD ≈ {sspRate.toLocaleString()} SSP</span>}
                        </div>
                        {config.donate_mobile_money_provider && <Row label="Provider" value={config.donate_mobile_money_provider} />}
                        {config.donate_mobile_money_number && <Row label="Number" value={config.donate_mobile_money_number} mono />}
                        {config.donate_mobile_money_name && <Row label="Account name" value={config.donate_mobile_money_name} />}
                        {sspRate > 0 && (
                            <div className="mt-4 pt-4 border-t border-border">
                                <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2">Common amounts in SSP</div>
                                <div className="grid grid-cols-4 gap-2 text-center text-sm">
                                    {[25, 50, 100, 250].map((u) => (
                                        <div key={u} className="border border-border rounded-sm py-2">
                                            <div className="text-xs text-muted-foreground">${u}</div>
                                            <div className="font-mono text-xs mt-1">{Math.round(u * sspRate).toLocaleString()}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        <p className="text-xs text-muted-foreground mt-4">
                            After sending, please email us at <a href={`mailto:${config.contact_email}`} className="underline text-primary">{config.contact_email}</a> with the transaction reference so we can issue a receipt.
                        </p>
                    </div>

                    {(config.donate_bank_name || config.donate_bank_account_number) && (
                        <div className="bg-card border border-border rounded-sm p-6">
                            <div className="flex items-center gap-3 mb-3">
                                <Landmark size={18} className="text-primary" />
                                <h3 className="font-display text-xl">Bank Transfer</h3>
                            </div>
                            {config.donate_bank_name && <Row label="Bank" value={config.donate_bank_name} />}
                            {config.donate_bank_account_name && <Row label="Account name" value={config.donate_bank_account_name} />}
                            {config.donate_bank_account_number && <Row label="Account #" value={config.donate_bank_account_number} mono />}
                            {config.donate_bank_swift && <Row label="SWIFT / BIC" value={config.donate_bank_swift} mono />}
                            {config.donate_bank_branch && <Row label="Branch" value={config.donate_bank_branch} />}
                        </div>
                    )}

                    <Link to="/contact" className="block bg-muted/40 border border-border rounded-sm p-6 hover:border-primary/60 transition-colors">
                        <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground mb-2">Major giving / partnerships</div>
                        <div className="font-display text-xl mb-1">Talk to our team</div>
                        <div className="text-sm text-muted-foreground inline-flex items-center gap-1">Get in touch <ArrowRight size={14} /></div>
                    </Link>
                </aside>
            </section>
        </div>
    );
}

const Row = ({ label, value, mono }) => (
    <div className="flex items-center justify-between py-2 border-t border-border first:border-t-0">
        <span className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{label}</span>
        <span className={`text-sm ${mono ? "font-mono" : "font-medium"}`}>{value}</span>
    </div>
);
